import { useEffect, useId, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

interface School { id: string; name: string; parish: string; type: string | null }

interface Props {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  name?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  onBlur?: () => void;
  "data-testid"?: string;
}

/**
 * School name with suggestions from the Jamaican school list (GET /api/schools). Free text is still
 * allowed: a student whose school is not listed keeps whatever they typed.
 */
export function SchoolAutocomplete({ value, onChange, id, name, placeholder = "School name", className, disabled, onBlur, ...rest }: Props) {
  const [items, setItems] = useState<School[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [picked, setPicked] = useState<string | null>(null);
  const listId = useId();
  const box = useRef<HTMLDivElement>(null);
  const abort = useRef<AbortController | null>(null);

  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 2 || q === picked) { setItems([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      abort.current?.abort();
      const ac = new AbortController();
      abort.current = ac;
      try {
        const r = await fetch(`/api/schools?q=${encodeURIComponent(q)}`, { signal: ac.signal });
        const d = await r.json();
        const list: School[] = (d.schools ?? []).slice(0, 8);
        setItems(list);
        setOpen(list.length > 0);
        setActive(-1);
      } catch { /* typing continues; suggestions are optional */ }
    }, 180);
    return () => clearTimeout(t);
  }, [value, picked]);

  useEffect(() => {
    const close = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const pick = (s: School) => { setPicked(s.name); onChange(s.name); setOpen(false); setItems([]); };

  return (
    <div ref={box} className="relative">
      <Input
        id={id}
        name={name}
        value={value}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        onChange={(e) => { setPicked(null); onChange(e.target.value); }}
        onFocus={() => { if (items.length) setOpen(true); }}
        onBlur={onBlur}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(items[active]); }
          else if (e.key === "Escape") setOpen(false);
        }}
        {...rest}
      />
      {open && (
        <ul id={listId} role="listbox" className="absolute z-50 mt-1 w-full max-h-64 overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md">
          {items.map((s, i) => (
            <li
              key={s.id}
              role="option"
              aria-selected={i === active}
              className={`cursor-pointer px-3 py-2 text-sm ${i === active ? "bg-accent text-accent-foreground" : ""}`}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setActive(i)}
              data-testid="school-suggestion"
            >
              <div className="font-medium">{s.name}</div>
              <div className="text-xs text-muted-foreground">{[s.type, s.parish].filter(Boolean).join(" · ")}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
